import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, CircularProgress, Divider, Drawer, Typography } from '@mui/material';
import { CancelIcon } from '@sharedCR/coreDesignSystem/icons';
import StickyHeader from 'app/shared/componentsRestyled/common/StickyHeader';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconOrientation, IconSize } from 'constants/coreDesignSystem';
import { LoanChangesHistoryState } from 'constants/loanApplication';
import { CURRENT_USER_AUTHOR_LABEL } from 'constants/pendingChangesAuthors';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getConditionalObject } from 'utils/data';
import {
  annotatePendingChanges,
  buildAuthorColorMap,
  getPendingChangeAuthorKey,
} from 'utils/pendingChangesAuthors';

import {
  getLoanActionsHistory,
  setActiveTabWithNavigationAction,
} from 'store/loanApplications/actions';

import { ApplicationChangesHistoryActionType } from '../../LoanInfoHeader/LoanChangesHistoryTable/LoanChangesHistoryTable.constants';
import {
  normalizeLoanActionsHistoryTableData,
  normalizeLoanChangesHistoryTableData,
} from '../../LoanInfoHeader/LoanChangesHistoryTable/LoanChangesHistoryTable.utils';
import useCustomFieldHistoryContext from '../hooks/useCustomFieldHistoryContext';
import useDrawerAuthorKeys from '../hooks/useDrawerAuthorKeys';
import useFeeHistoryContext from '../hooks/useFeeHistoryContext';
import usePendingChangesAuthorsControls from '../hooks/usePendingChangesAuthorsControls';
import PendingChangesFilterBar from '../PendingChangesDrawer/components/PendingChangesFilterBar';
import PendingChangesOperationGroup from '../PendingChangesDrawer/components/PendingChangesOperationGroup';
import PendingChangesSectionGroup from '../PendingChangesDrawer/components/PendingChangesSectionGroup';
import PendingChangesSidebar from '../PendingChangesDrawer/components/PendingChangesSidebar';
import {
  ALL_SECTIONS_KEY,
  ChangeKindFilter,
} from '../PendingChangesDrawer/PendingChangesDrawer.constants';
import {
  changesSummaryStyles,
  expandCollapseButtonStyles,
} from '../PendingChangesDrawer/PendingChangesDrawer.styles';
import {
  buildDrawerAuthorsFromChanges,
  buildOperationGroups,
  buildSectionTreesByKind,
  checkHasCauseInformation,
  filterChangesByAuthor,
  filterChangesByAuthorKeys,
  filterOperationGroups,
  filterOperationGroupsBySearch,
  filterOperationGroupsBySection,
  getChangeKindCounts,
  getChangeType,
  getMenuChangesByKind,
  getSectionKeptInTree,
  getSectionSummary,
  getUniqueAuthors,
  groupChangesBySection,
  labelAiChanges,
} from '../PendingChangesDrawer/PendingChangesDrawer.utils';

import LogHistoryDateFilter from './components/LogHistoryDateFilter';
import { DEFAULT_DATE_RANGE_DAYS, paperProps, slideProps } from './LogHistoryDrawer.constants';
import {
  bodyStyles,
  closeButtonStyles,
  contentWrapperStyles,
  drawerStyles,
  emptyStateStyles,
  headerActionsStyles,
  headerStyles,
  headerTitleStyles,
  loaderWrapperStyles,
  mainContentStyles,
  progressStyles,
} from './LogHistoryDrawer.styles';
import { LogHistoryDrawerTestIds } from './LogHistoryDrawer.testIds';
import {
  buildExpandedGroupKeys,
  filterChangesByDateRange,
  getListedChanges,
  getRecalculatedCardTitle,
  getSearchExpandedGroupKeys,
} from './LogHistoryDrawer.utils';

const LogHistoryDrawer = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const loanId = useLoanId();

  const customFieldContext = useCustomFieldHistoryContext();
  const feeTypeContext = useFeeHistoryContext();

  const {
    isEnabled: isPendingChangesByUserEnabled,
    currentUserId,
    authors: pendingChangesAuthors,
  } = usePendingChangesAuthorsControls();

  const [rawLoanChangesHistory, setRawLoanChangesHistory] = useState([]);
  const [rawLoanActionsHistory, setRawLoanActionsHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(ALL_SECTIONS_KEY);
  const [changeKindFilter, setChangeKindFilter] = useState(ChangeKindFilter.DIRECT);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(DEFAULT_DATE_RANGE_DAYS, 'days'));
  const [toDate, setToDate] = useState(dayjs());
  const [expandedGroupKeys, setExpandedGroupKeys] = useState({});

  const changes = useMemo(() => {
    const normalizedChanges = normalizeLoanChangesHistoryTableData(
      isPendingChangesByUserEnabled
        ? annotatePendingChanges(rawLoanChangesHistory)
        : rawLoanChangesHistory,
      customFieldContext,
      feeTypeContext,
    ).concat(
      // Loan actions are keyed by their author too, so a person's actions and changes share one chip.
      normalizeLoanActionsHistoryTableData(
        isPendingChangesByUserEnabled
          ? rawLoanActionsHistory.map(action => ({
              ...action,
              authorKey: getPendingChangeAuthorKey(action),
            }))
          : rawLoanActionsHistory,
      ),
    );

    const typedChanges = normalizedChanges.map(change => ({
      ...change,
      changeType: getChangeType(change.actionType),
    }));

    return isPendingChangesByUserEnabled ? labelAiChanges(typedChanges) : typedChanges;
  }, [
    isPendingChangesByUserEnabled,
    rawLoanChangesHistory,
    rawLoanActionsHistory,
    customFieldContext,
    feeTypeContext,
  ]);

  const filteredByDate = useMemo(
    () => filterChangesByDateRange(changes, fromDate, toDate),
    [changes, fromDate, toDate],
  );

  const hasCauseInformation = useMemo(
    () => checkHasCauseInformation(filteredByDate),
    [filteredByDate],
  );

  const operationGroups = useMemo(() => buildOperationGroups(filteredByDate), [filteredByDate]);

  const authors = useMemo(
    () =>
      isPendingChangesByUserEnabled
        ? buildDrawerAuthorsFromChanges(getListedChanges(operationGroups), {
            currentUserId,
            currentUserLabel: CURRENT_USER_AUTHOR_LABEL,
          })
        : getUniqueAuthors(getListedChanges(operationGroups)),
    [isPendingChangesByUserEnabled, operationGroups, currentUserId],
  );

  // People with pending changes come first, so each keeps the colour they have on the loan.
  const authorColorMap = useMemo(
    () =>
      isPendingChangesByUserEnabled
        ? buildAuthorColorMap(
            _.uniqBy([...pendingChangesAuthors, ...authors], 'key'),
            ({ key }) => key,
          )
        : buildAuthorColorMap(authors),
    [isPendingChangesByUserEnabled, pendingChangesAuthors, authors],
  );

  const allAuthorKeys = useMemo(() => authors.map(({ key }) => key), [authors]);

  // Like the Pending Changes drawer, the chips tick people in and out of this list; it opens on everyone.
  const {
    selectedAuthorKeys: drawerAuthorKeys,
    onAuthorToggle: onDrawerAuthorToggle,
    onResetAuthorKeys,
  } = useDrawerAuthorKeys(allAuthorKeys);

  const selectedAuthorKeys = useMemo(
    () => (isPendingChangesByUserEnabled ? drawerAuthorKeys : _.compact([selectedAuthor])),
    [isPendingChangesByUserEnabled, drawerAuthorKeys, selectedAuthor],
  );

  const onAuthorToggle = useCallback(
    authorKey => {
      if (isPendingChangesByUserEnabled) {
        onDrawerAuthorToggle(authorKey);

        return;
      }

      setSelectedAuthor(prevAuthor => (prevAuthor === authorKey ? null : authorKey));
    },
    [isPendingChangesByUserEnabled, onDrawerAuthorToggle],
  );

  const filterByAuthor = useCallback(
    changesToFilter =>
      isPendingChangesByUserEnabled
        ? filterChangesByAuthorKeys(changesToFilter, selectedAuthorKeys)
        : filterChangesByAuthor(changesToFilter, selectedAuthor),
    [isPendingChangesByUserEnabled, selectedAuthorKeys, selectedAuthor],
  );

  const operationGroupsByAuthor = useMemo(
    () => filterOperationGroups(operationGroups, filterByAuthor),
    [operationGroups, filterByAuthor],
  );

  const operationGroupsBySearch = useMemo(
    () => filterOperationGroupsBySearch(operationGroupsByAuthor, searchQuery),
    [operationGroupsByAuthor, searchQuery],
  );

  const displayedOperationGroups = useMemo(
    () =>
      filterOperationGroupsBySection(operationGroupsBySearch, selectedSection, changeKindFilter),
    [operationGroupsBySearch, selectedSection, changeKindFilter],
  );

  const menuChangesByKind = useMemo(
    () => getMenuChangesByKind(operationGroupsByAuthor, searchQuery),
    [operationGroupsByAuthor, searchQuery],
  );

  const changeKindCounts = useMemo(
    () => getChangeKindCounts(menuChangesByKind),
    [menuChangesByKind],
  );

  const sectionTreesByKind = useMemo(
    () => buildSectionTreesByKind(menuChangesByKind),
    [menuChangesByKind],
  );

  const isRecalculatedSelected = changeKindFilter === ChangeKindFilter.RECALCULATED;

  const listedChanges = useMemo(
    () => getListedChanges(operationGroupsBySearch),
    [operationGroupsBySearch],
  );

  const displayedChanges = useMemo(
    () => getListedChanges(displayedOperationGroups),
    [displayedOperationGroups],
  );

  const hasAffectedChanges = useMemo(
    () => displayedOperationGroups.some(({ affectedChanges }) => affectedChanges.length > 0),
    [displayedOperationGroups],
  );

  const resolvedExpandedGroupKeys = useMemo(
    () => ({
      ...getSearchExpandedGroupKeys(operationGroupsBySearch, searchQuery),
      ...getConditionalObject(isRecalculatedSelected, () =>
        buildExpandedGroupKeys(displayedOperationGroups, true),
      ),
      ...expandedGroupKeys,
    }),
    [
      operationGroupsBySearch,
      searchQuery,
      isRecalculatedSelected,
      displayedOperationGroups,
      expandedGroupKeys,
    ],
  );

  const displayGroupedChanges = useMemo(
    () => groupChangesBySection(displayedChanges),
    [displayedChanges],
  );

  const onSearch = useCallback(query => {
    setSearchQuery(query);
    setExpandedGroupKeys({});
  }, []);

  const onSectionSelect = useCallback(sectionKey => {
    setSelectedSection(sectionKey);
    setExpandedGroupKeys({});
  }, []);

  const onChangeKindSelect = useCallback(
    kind => {
      setChangeKindFilter(kind);
      setSelectedSection(prev => getSectionKeptInTree(sectionTreesByKind[kind], prev));
      setExpandedGroupKeys({});
    },
    [sectionTreesByKind],
  );

  const onToggleAffected = useCallback(
    groupKey => {
      setExpandedGroupKeys(prev => ({ ...prev, [groupKey]: !resolvedExpandedGroupKeys[groupKey] }));
    },
    [resolvedExpandedGroupKeys],
  );

  const onExpandAllClick = useCallback(() => {
    setExpandedGroupKeys(buildExpandedGroupKeys(displayedOperationGroups, true));
  }, [displayedOperationGroups]);

  const onCollapseAllClick = useCallback(() => {
    setExpandedGroupKeys(buildExpandedGroupKeys(displayedOperationGroups, false));
  }, [displayedOperationGroups]);

  const handleNavigate = useCallback(
    change => {
      onClose();

      if (!_.isNil(change.navigation)) {
        dispatch(
          setActiveTabWithNavigationAction({
            ...change.navigation,
            loanId,
            applicationId,
          }),
        );

        return;
      }

      if (_.isNil(change.tabName)) {
        return;
      }

      dispatch(
        setActiveTabWithNavigationAction({
          activeTab: change.tabName,
          loanId,
          applicationId,
        }),
      );
    },
    [dispatch, onClose, loanId, applicationId],
  );

  useEffectSafeStrictMode(() => {
    if (!isOpen) {
      return;
    }

    setIsLoading(true);
    setSelectedSection(ALL_SECTIONS_KEY);
    setChangeKindFilter(ChangeKindFilter.DIRECT);
    setSelectedAuthor(null);
    onResetAuthorKeys();
    setSearchQuery('');
    setFromDate(dayjs().subtract(DEFAULT_DATE_RANGE_DAYS, 'days'));
    setToDate(dayjs());
    setExpandedGroupKeys({});

    const action = ApplicationChangesHistoryActionType[LoanChangesHistoryState.APPLIED];

    const promises = [
      dispatch(action({ applicationId, historyState: LoanChangesHistoryState.APPLIED })),
      dispatch(getLoanActionsHistory({ applicationId })),
    ];

    Promise.all(promises)
      .then(([{ payload: changesHistory }, { payload: loanActionsHistory }]) => {
        const loanChangesHistoryFlattened = Object.values(changesHistory || {}).flat();
        setRawLoanChangesHistory(loanChangesHistoryFlattened);
        setRawLoanActionsHistory(loanActionsHistory || []);
      })
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, isOpen, applicationId, onResetAuthorKeys]);

  return (
    <Drawer
      PaperProps={paperProps}
      SlideProps={slideProps}
      anchor="right"
      data-testid={LogHistoryDrawerTestIds.DRAWER}
      open={isOpen}
      sx={drawerStyles}
      onClose={onClose}
    >
      <Box sx={contentWrapperStyles}>
        <Box sx={headerStyles}>
          <Typography sx={headerTitleStyles} variant="bold16">
            Log History
          </Typography>
          <Box sx={headerActionsStyles}>
            <Button
              data-testid={LogHistoryDrawerTestIds.CLOSE_BUTTON}
              sx={closeButtonStyles}
              variant="text"
              onClick={onClose}
            >
              <CancelIcon size={IconSize[18]} />
            </Button>
          </Box>
        </Box>

        <Box sx={mainContentStyles}>
          <PendingChangesSidebar
            sections={sectionTreesByKind[changeKindFilter]}
            selectedKind={changeKindFilter}
            selectedSection={selectedSection}
            totalCounts={changeKindCounts}
            onKindSelect={onChangeKindSelect}
            onSectionSelect={onSectionSelect}
          />

          <Divider orientation={IconOrientation.VERTICAL} />

          <Box sx={bodyStyles}>
            {isLoading && (
              <Box sx={loaderWrapperStyles}>
                <CircularProgress size={50} sx={progressStyles} />
              </Box>
            )}

            {!isLoading && (
              <>
                <StickyHeader>
                  <LogHistoryDateFilter
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={setFromDate}
                    onToDateChange={setToDate}
                  />
                  <PendingChangesFilterBar
                    authorColorMap={authorColorMap}
                    authors={authors}
                    selectedAuthorKeys={selectedAuthorKeys}
                    onAuthorToggle={onAuthorToggle}
                    onSearch={onSearch}
                  />
                </StickyHeader>

                {!filteredByDate.length && (
                  <Box sx={emptyStateStyles}>
                    <Typography variant="regular14">
                      No log history found for the selected date range
                    </Typography>
                  </Box>
                )}

                {!!filteredByDate.length && (
                  <>
                    {hasCauseInformation && hasAffectedChanges && (
                      <Box sx={changesSummaryStyles}>
                        <Button
                          data-testid={LogHistoryDrawerTestIds.EXPAND_ALL_BUTTON}
                          sx={expandCollapseButtonStyles}
                          variant="outlined"
                          onClick={onExpandAllClick}
                        >
                          Expand all
                        </Button>
                        <Button
                          data-testid={LogHistoryDrawerTestIds.COLLAPSE_ALL_BUTTON}
                          sx={expandCollapseButtonStyles}
                          variant="outlined"
                          onClick={onCollapseAllClick}
                        >
                          Collapse all
                        </Button>
                      </Box>
                    )}

                    {!listedChanges.length && (
                      <Box sx={emptyStateStyles}>
                        <Typography variant="regular14">
                          No log history matches the current filters
                        </Typography>
                      </Box>
                    )}

                    {hasCauseInformation &&
                      displayedOperationGroups.map(group => (
                        <PendingChangesOperationGroup
                          isExactTimeShown
                          authorColorMap={authorColorMap}
                          group={group}
                          isAffectedExpanded={!!resolvedExpandedGroupKeys[group.key]}
                          key={group.key}
                          recalculatedCardTitle={getRecalculatedCardTitle(group)}
                          onNavigate={handleNavigate}
                          onToggleAffected={onToggleAffected}
                        />
                      ))}

                    {!hasCauseInformation &&
                      Object.entries(displayGroupedChanges).map(([sectionName, sectionChanges]) => {
                        const summary = getSectionSummary(sectionChanges);

                        return (
                          <PendingChangesSectionGroup
                            authorColorMap={authorColorMap}
                            changeCount={summary.changeCount}
                            changes={sectionChanges}
                            key={sectionName}
                            sectionName={sectionName}
                            userCount={summary.userCount}
                            onNavigate={handleNavigate}
                          />
                        );
                      })}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

LogHistoryDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(LogHistoryDrawer);
